import { useState, useEffect, useCallback } from 'react';
import './RadarTab.css';
import { supabase } from './supabase';
import { useEffectiveUser } from './ImpersonationContext';
import Badge from './components/Badge';
import StatCard from './components/StatCard';

const VPS = 'https://copyccxx.com';

function timeAgo(isoTs) {
  if (!isoTs) return '—';
  const diff = Math.floor((Date.now() - new Date(isoTs).getTime()) / 1000);
  if (diff < 60)    return 'il y a quelques secondes';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)} jours`;
}

const truncAddr = a => `${a.slice(0, 6)}…${a.slice(-4)}`;
const fmtUsd = v => v == null ? '—' : `$${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}`;
const fmtPct = v => v == null ? '—' : `${v >= 0 ? '+' : ''}${Number(v).toFixed(0)}%`;

const DEFAULT_SETTINGS = {
  enabled:                false,
  min_amount_usd:         500,
  tier1_max_min:          15,
  tier2_max_min:          60,
  min_gain_pct:           50,
  min_token_gain_24h_pct: 50,
  max_tokens_per_day:     60,
  max_wallets:            500,
  min_wallet_age_days:    7,
};

// SVG cohérents avec AnalyticsTab (stroke #00e5cc, 16×16)
const IconRadar = (
  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
    <circle cx="10" cy="10" r="2" fill="#00e5cc"/>
    <circle cx="10" cy="10" r="5" stroke="#00e5cc" strokeWidth="1.3" opacity=".6"/>
    <circle cx="10" cy="10" r="8" stroke="#00e5cc" strokeWidth="1.3" opacity=".3"/>
  </svg>
);
const IconSniper = (
  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
    <circle cx="10" cy="10" r="6" stroke="#00e5cc" strokeWidth="1.3"/>
    <circle cx="10" cy="10" r="2" fill="#00e5cc"/>
    <path d="M10 2v3M10 15v3M2 10h3M15 10h3" stroke="#00e5cc" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const IconEarly = (
  <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
    <path d="M11 2L4 11h7l-2 7 9-10h-7l2-6z" stroke="#00e5cc" strokeWidth="1.3" strokeLinejoin="round"/>
  </svg>
);

export default function RadarTab() {
  const { effectiveUserId, isImpersonating } = useEffectiveUser();

  const [wallets,        setWallets]        = useState([]);
  const [settings,       setSettings]       = useState(null);
  const [lastScan,       setLastScan]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [settingsForm,   setSettingsForm]   = useState(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [rescoringMsg,   setRescoringMsg]   = useState(null);
  const [filterStatus,   setFilterStatus]   = useState('all');

  const loadAll = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = effectiveUserId || session?.user?.id;
    if (!userId) { setLoading(false); return; }

    const [{ data: ws }, { data: st }, { data: log }] = await Promise.all([
      supabase.from('radar_wallets')
        .select('*')
        .eq('user_id', userId)
        .order('follow_score',    { ascending: false, nullsFirst: false })
        .order('discovery_score', { ascending: false }),
      supabase.from('radar_settings')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle(),
      supabase.from('radar_scan_log')
        .select('scanned_at, tokens_scanned, wallets_added, wallets_evicted')
        .eq('user_id', userId)
        .eq('status', 'success')
        .order('scanned_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    setWallets(ws || []);
    setSettings(st || null);
    setSettingsForm(st ? { ...DEFAULT_SETTINGS, ...st } : DEFAULT_SETTINGS);
    setLastScan(log || null);
    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    setLoading(true);
    loadAll();

    let sub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      const userId = effectiveUserId || session?.user?.id;
      if (!userId) return;
      sub = supabase.channel(`radar-wallets-${userId}`)
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'radar_wallets', filter: `user_id=eq.${userId}` },
          loadAll)
        .subscribe();
    });
    return () => { sub?.unsubscribe(); };
  }, [effectiveUserId, loadAll]);

  const handleToggle = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    const newEnabled = !(settings?.enabled ?? false);
    await supabase.from('radar_settings').upsert(
      { user_id: userId, ...DEFAULT_SETTINGS, ...(settings || {}), enabled: newEnabled },
      { onConflict: 'user_id' }
    );
    setSettings(s => ({ ...DEFAULT_SETTINGS, ...(s || {}), enabled: newEnabled }));
  };

  const handleDeleteWallet = async (wallet) => {
    if (isImpersonating) return;
    if (!window.confirm(`Supprimer ${truncAddr(wallet.wallet_address)} du Radar ?`)) return;
    setWallets(prev => prev.filter(w => w.id !== wallet.id));
    await supabase.from('radar_wallets').delete().eq('id', wallet.id);
  };

  const handleRescore = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    setRescoringMsg(null);
    try {
      const r = await fetch(`${VPS}/api/radar/rescore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (r.ok) {
        setRescoringMsg('Rescoring lancé — résultats disponibles sous quelques minutes.');
        setTimeout(() => setRescoringMsg(null), 8000);
      } else {
        const e = await r.json().catch(() => ({}));
        setRescoringMsg(`Erreur : ${e.error || r.status}`);
      }
    } catch (e) {
      setRescoringMsg(`Erreur réseau : ${e.message}`);
    }
  };

  const handleSaveSettings = async () => {
    if (isImpersonating) return;
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;
    setSavingSettings(true);
    const payload = {
      user_id:                userId,
      enabled:                settings?.enabled ?? false,
      min_amount_usd:         Number(settingsForm.min_amount_usd),
      tier1_max_min:          Number(settingsForm.tier1_max_min),
      tier2_max_min:          Number(settingsForm.tier2_max_min),
      min_gain_pct:           Number(settingsForm.min_gain_pct),
      min_token_gain_24h_pct: Number(settingsForm.min_token_gain_24h_pct),
      max_tokens_per_day:     Number(settingsForm.max_tokens_per_day),
      max_wallets:            Number(settingsForm.max_wallets),
      min_wallet_age_days:    Number(settingsForm.min_wallet_age_days),
    };
    const { data } = await supabase.from('radar_settings')
      .upsert(payload, { onConflict: 'user_id' })
      .select()
      .maybeSingle();
    if (data) setSettings(data);
    setSavingSettings(false);
  };

  const enabled        = settings?.enabled ?? false;
  const sniperCount    = wallets.filter(w => w.tier === 'sniper').length;
  const earlyCount     = wallets.filter(w => w.tier === 'early_mover').length;
  const watchingCount  = wallets.filter(w => (w.discovery_status ?? 'validated') === 'watching').length;
  const visibleWallets = filterStatus === 'all'
    ? wallets
    : wallets.filter(w => (w.discovery_status ?? 'validated') === filterStatus);

  if (loading) {
    return <div className="rd-root"><div className="rd-loading">Chargement du Radar…</div></div>;
  }

  return (
    <div className="rd-root">

      {/* ── Header ── */}
      <div className="rd-header">
        <div className="rd-header-left">
          <button
            className={`rd-toggle ${enabled ? 'rd-toggle--on' : 'rd-toggle--off'}`}
            onClick={handleToggle}
            disabled={isImpersonating}
            title={enabled ? 'Désactiver le Radar' : 'Activer le Radar'}
          >
            <span className="rd-toggle-thumb" />
          </button>
          <span className="rd-toggle-label">
            Radar <span className={enabled ? 'rd-on' : 'rd-off'}>{enabled ? 'ACTIF' : 'DÉSACTIVÉ'}</span>
          </span>
          {lastScan ? (
            <span className="rd-lastscan">
              · Dernier scan {timeAgo(lastScan.scanned_at)}
              <span className="rd-lastscan-detail">
                &nbsp;({lastScan.tokens_scanned} tokens · +{lastScan.wallets_added} wallets)
              </span>
            </span>
          ) : (
            <span className="rd-lastscan">· Aucun scan effectué</span>
          )}
        </div>
        <div className="rd-header-right">
          <button className="rd-btn rd-btn--ghost" onClick={() => setSettingsOpen(o => !o)}>
            ⚙ Réglages
          </button>
          <button
            className="rd-btn rd-btn--accent"
            onClick={handleRescore}
            disabled={isImpersonating || wallets.length === 0}
            title={wallets.length === 0 ? 'Aucun wallet à scorer' : ''}
          >
            ⟳ Relancer le scoring
          </button>
        </div>
      </div>

      {/* ── Feedback rescore ── */}
      {rescoringMsg && (
        <div className="rd-banner rd-banner--teal">{rescoringMsg}</div>
      )}

      {/* ── Banner désactivé ── */}
      {!enabled && (
        <div className="rd-banner rd-banner--warning">
          Radar désactivé — aucun scan automatique ne sera effectué. Active le toggle pour démarrer la découverte quotidienne.
        </div>
      )}

      {/* ── Panneau réglages repliable ── */}
      {settingsOpen && (
        <div className="rd-settings-panel">
          <div className="rd-settings-title">Paramètres du scan</div>
          <div className="rd-settings-grid">
            {[
              { key: 'min_amount_usd',         label: 'Mise min.',                  unit: '$' },
              { key: 'tier1_max_min',          label: 'Sniper — fenêtre max',       unit: 'min' },
              { key: 'tier2_max_min',          label: 'Early mover — fenêtre max',  unit: 'min' },
              { key: 'min_gain_pct',           label: 'Gain wallet min.',           unit: '%' },
              { key: 'min_token_gain_24h_pct', label: 'Gain token h24 min.',        unit: '%' },
              { key: 'max_tokens_per_day',     label: 'Tokens analysés / jour',     unit: '' },
              { key: 'max_wallets',            label: 'Wallets max en base',        unit: '' },
              { key: 'min_wallet_age_days',    label: 'Protection nouvelles découvertes', unit: 'j' },
            ].map(({ key, label, unit }) => (
              <label key={key} className="rd-setting-field">
                <span className="rd-setting-label">{label}</span>
                <div className="rd-setting-input-wrap">
                  <input
                    type="number"
                    className="rd-setting-input"
                    value={settingsForm[key] ?? ''}
                    onChange={e => setSettingsForm(f => ({ ...f, [key]: e.target.value }))}
                    disabled={isImpersonating}
                  />
                  {unit && <span className="rd-setting-unit">{unit}</span>}
                </div>
              </label>
            ))}
          </div>
          <button
            className="rd-btn rd-btn--save"
            onClick={handleSaveSettings}
            disabled={savingSettings || isImpersonating}
          >
            {savingSettings ? 'Sauvegarde…' : 'Sauvegarder les réglages'}
          </button>
        </div>
      )}

      {/* ── StatCards ── */}
      <div className="rd-stats-row">
        <StatCard
          label="Wallets découverts"
          value={wallets.length}
          sub={`sur ${settings?.max_wallets ?? 500} max`}
          trend="neutral"
          icon={IconRadar}
        />
        <StatCard
          label="Snipers"
          value={sniperCount}
          sub="entrée ≤ tier1"
          trend={sniperCount > 0 ? 'up' : 'neutral'}
          icon={IconSniper}
        />
        <StatCard
          label="Early Movers"
          value={earlyCount}
          sub="entrée tier1 → tier2"
          trend={earlyCount > 0 ? 'accent' : 'neutral'}
          icon={IconEarly}
        />
      </div>

      {/* ── Filter bar ── */}
      {wallets.length > 0 && (
        <div className="rd-filter-bar">
          {[
            { key: 'all',       label: 'Tous',         count: wallets.length },
            { key: 'validated', label: 'Validés',      count: wallets.length - watchingCount },
            { key: 'watching',  label: 'À surveiller', count: watchingCount },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              className={`rd-filter-btn${filterStatus === key ? ' rd-filter-btn--active' : ''}`}
              onClick={() => setFilterStatus(key)}
            >
              {label}
              <span className="rd-filter-count">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Table wallets ── */}
      {wallets.length === 0 ? (
        <div className="rd-empty">
          {enabled
            ? 'Aucun wallet découvert pour l\'instant — le scanner tournera cette nuit.'
            : 'Active le Radar pour commencer la découverte automatique de wallets.'}
        </div>
      ) : visibleWallets.length === 0 ? (
        <div className="rd-empty rd-empty--filtered">Aucun wallet dans cette catégorie.</div>
      ) : (
        <div className={`rd-table-wrap${!enabled ? ' rd-table-wrap--dimmed' : ''}`}>
          <table className="rd-table">
            <thead>
              <tr>
                <th>Wallet</th>
                <th>Tier</th>
                <th>Token découverte</th>
                <th>Gain token</th>
                <th>Mise</th>
                <th>Score radar</th>
                <th>Score fiabilité</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleWallets.map(w => (
                <tr key={w.id} className="rd-row">
                  <td className="rd-cell-addr">
                    <a
                      href={`https://solscan.io/account/${w.wallet_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rd-addr-link"
                    >
                      {truncAddr(w.wallet_address)}
                    </a>
                  </td>
                  <td>
                    <div className="rd-tier-cell">
                      {w.tier === 'sniper'
                        ? <Badge variant="live">SNIPER</Badge>
                        : <Badge variant="warning">EARLY</Badge>}
                      {(w.discovery_status ?? 'validated') === 'watching' && (
                        <Badge variant="neutral">À SURVEILLER</Badge>
                      )}
                    </div>
                  </td>
                  <td className="rd-cell-token">
                    <span className="rd-token-sym">{w.discovery_token_symbol || '—'}</span>
                    {w.entry_delay_min != null && (
                      <span className="rd-token-delay">+{w.entry_delay_min}min</span>
                    )}
                  </td>
                  <td className={`rd-cell-num ${(w.gain_pct ?? 0) >= 0 ? 'rd-pos' : 'rd-neg'}`}>
                    {fmtPct(w.gain_pct)}
                  </td>
                  <td className="rd-cell-num">{fmtUsd(w.cumul_amount_usd)}</td>
                  <td>
                    <span className="rd-score-disc">{w.discovery_score ?? '—'}</span>
                  </td>
                  <td>
                    {w.follow_score != null ? (
                      <span className="rd-follow-wrap">
                        <span className={`rd-follow-score rd-follow-score--${
                            w.follow_recommendation === 'Suivre'        ? 'green'
                          : w.follow_recommendation === 'Ne pas suivre' ? 'red'
                          : 'orange'
                        }`}>
                          {w.follow_score}
                        </span>
                        <span className="rd-follow-rec">{w.follow_recommendation}</span>
                      </span>
                    ) : (
                      <span className="rd-not-scored">Pas encore scoré</span>
                    )}
                  </td>
                  <td className="rd-cell-del">
                    <button
                      className="rd-del-btn"
                      onClick={() => handleDeleteWallet(w)}
                      disabled={isImpersonating}
                      title="Supprimer ce wallet du Radar"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
